const ADMIN_API_BASE = 'https://run-ai-1.preview.emergentagent.com/api/admin';
const ADMIN_API_KEY = 'tempo-admin-ak-2f8d9e3c7a1b';

class AdminAPI {
  constructor() {
    this.baseUrl = ADMIN_API_BASE;
  }

  getToken() {
    return localStorage.getItem('tempo_admin_token');
  }

  setToken(token) {
    localStorage.setItem('tempo_admin_token', token);
  }

  clearToken() {
    localStorage.removeItem('tempo_admin_token');
    localStorage.removeItem('tempo_admin_user');
  }

  getAdmin() {
    try {
      const data = localStorage.getItem('tempo_admin_user');
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  setAdmin(admin) {
    localStorage.setItem('tempo_admin_user', JSON.stringify(admin));
  }

  async request(endpoint, options = {}) {
    const token = this.getToken();
    const headers = {
      'Content-Type': 'application/json',
      'X-API-Key': ADMIN_API_KEY,
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 401 || response.status === 403) {
      this.clearToken();
      window.location.href = '/admin/login';
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Auth
  async login(email, password) {
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.token);
    this.setAdmin(data.admin);
    return data;
  }

  // Users
  async getUsers(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/users?${query}`);
  }

  async getUserDetail(userId) {
    return this.request(`/users/${userId}`);
  }

  async disableUser(userId) {
    return this.request(`/users/${userId}/disable`, { method: 'PUT' });
  }

  async enableUser(userId) {
    return this.request(`/users/${userId}/enable`, { method: 'PUT' });
  }

  async deleteUser(userId) {
    return this.request(`/users/${userId}`, { method: 'DELETE' });
  }

  // Analytics
  async getAnalyticsOverview() {
    return this.request('/analytics/overview');
  }

  async getAnalyticsDaily(days = 30) {
    return this.request(`/analytics/daily?days=${days}`);
  }

  async getAnalyticsRetention() {
    return this.request('/analytics/retention');
  }

  // Coach Config
  async getCoachConfig() {
    return this.request('/coach/config');
  }

  async updateCoachConfig(config) {
    return this.request('/coach/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  // Notifications
  async sendNotification(notification) {
    return this.request('/notifications/send', {
      method: 'POST',
      body: JSON.stringify(notification),
    });
  }

  async getNotifications(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/notifications?${query}`);
  }

  // Templates
  async getTemplates() {
    return this.request('/templates');
  }

  async createTemplate(template) {
    return this.request('/templates', {
      method: 'POST',
      body: JSON.stringify(template),
    });
  }

  async deleteTemplate(templateId) {
    return this.request(`/templates/${templateId}`, { method: 'DELETE' });
  }

  // System
  async getHealth() {
    return this.request('/health');
  }

  async getAuditLog(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/audit?${query}`);
  }

  // Admin management
  async createAdmin(adminData) {
    return this.request('/auth/create', {
      method: 'POST',
      body: JSON.stringify(adminData),
    });
  }
}

export const adminAPI = new AdminAPI();
export default adminAPI;
